import dynamic from 'next/dynamic';
import { Fragment } from 'react';
import { NextSeo, BreadcrumbJsonLd } from 'next-seo';
import { useObjectsProvider } from '../../context/objects';
import { useRouter } from 'next/router';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import Button from '../../components/Button';
import Splitter from '../../components/Splitter';
import ObjectsCatalog from '../../components/objects/ObjectsCatalog';
import ObjectsFilter from '../../components/objects/ObjectsFilter';
import ObjectsFilterMobile from '../../components/objects/ObjectsFilterMobile';
import styles from '../../styles/modules/objects/Objects.module.scss';

const ObjectsSEO = dynamic(() => import('../../components/objects/ObjectsSEO'));

const isAnyFilters = (s) => {
  const arrFilters = [
    'priceFrom',
    'priceTo',
    'rooms',
    'studio',
    'squareFrom',
    'squareTo',
    'mikroraion',
    'squareAreaFrom',
    'squareAreaTo',
    'commercialType',
    'squareLivingFrom',
    'squareLivingTo',
    'squareKitchenFrom',
    'squareKitchenTo',
    'floorFrom',
    'floorTo',
    'floorsFrom',
    'floorsTo',
    'ceiling',
    'balcony',
    'renovation',
    'bathroom',
    'buildingType',
    'stock',
    'lotType',
    'subCategory',
    'entrance',
    'commercialBuildingType'
  ];
  let filtersTotal = 0;

  for (let i = 0; i < arrFilters.length; i++)
    if (s[arrFilters[i]] && (s[arrFilters[i]].length || s[arrFilters[i]] > 0)) filtersTotal++;

  return filtersTotal > 0;
};
const categoryToSEO = (c) => {
  switch (c) {
    default:
    case 'flats':
      return 'Квартиры';

    case 'rooms':
      return 'Комнаты';

    case 'houses':
      return 'Дома';

    case 'sites':
      return 'Участки';

    case 'commercial':
      return 'Коммерция';
  }
};
const flatsToSEO = (f) => {
  switch (f) {
    case 'studio':
      return 'Студии';

    default:
    case '1k':
      return '1-комнатные';

    case '2k':
      return '2-комнатные';

    case '3k':
      return '3-комнатные';

    case '4k':
      return '4-комнатные';
  }
};
const breadcrumbsGenerator = (q) => {
  const readyBreadcrumbs = [
    {
      position: 1,
      name: 'Вторичка',
      item: 'https://landlord.ru/objects'
    }
  ];

  if (q.path && q.path.length) {
    /** Type buy/rent */
    if (q.path[0]) {
      readyBreadcrumbs.push({
        position: 2,
        name: q.path[0] === 'rent' ? 'Аренда' : 'Продажа',
        item: `https://landlord.ru/objects/${q.path[0]}`
      });
    }

    /** Category flats/rooms/houses/sites/commercial */
    if (q.path[1]) {
      readyBreadcrumbs.push({
        position: 3,
        name: categoryToSEO(q.path[1]),
        item: `https://landlord.ru/objects/${q.path[0]}/${q.path[1]}`
      });
    }

    /** Rooms count for flats studio/1k/2k/3k/4k */
    if (q.path[1] && q.path[1] === 'flats' && q.path[2]) {
      readyBreadcrumbs.push({
        position: 4,
        name: flatsToSEO(q.path[2]),
        item: `https://landlord.ru/objects/${q.path[0]}/${q.path[1]}/${q.path[2]}`
      });
    }
  }

  return readyBreadcrumbs;
};

const ObjectsPage = ({ query }) => {
  const router = useRouter();
  const [objectsState, setObjectsState] = useObjectsProvider();

  const getSEOTitle = () => {
    const arrTitle = [];

    /** TYPE */
    arrTitle.push(query.path && query.path[0] && query.path[0] === 'rent' ? 'Снять' : 'Купить');

    /** CATEGORY */
    if (
      query.path &&
      query.path[1] &&
      ['flats', 'rooms', 'houses', 'sites', 'commercial'].includes(query.path[1])
    ) {
      switch (query.path[1]) {
        default:
        case 'flats':
          if (query.path[2] && ['studio', '1k', '2k', '3k', '4k'].includes(query.path[2])) {
            switch (query.path[2]) {
              case 'studio':
                arrTitle.push('квартиру-студию вторичку');
                break;

              case '1k':
                arrTitle.push('однокомнатную квартиру вторичку');
                break;

              case '2k':
                arrTitle.push('двухкомнатную квартиру вторичку');
                break;

              case '3k':
                arrTitle.push('трехкомнатную квартиру вторичку');
                break;

              case '4k':
                arrTitle.push('четырехкомнатную квартиру вторичку');
                break;

              default:
                break;
            }
          } else {
            arrTitle.push('квартиру вторичку');
          }

          break;

        case 'rooms':
          arrTitle.push('комнату');
          break;

        case 'houses':
          arrTitle.push('дом');
          break;

        case 'sites':
          arrTitle.push('участок');
          break;

        case 'commercial':
          arrTitle.push('коммерческую недвижимость');
          break;
      }
    } else {
      arrTitle.push('квартиру вторичку');
    }

    arrTitle.push('в Ростове-на-Дону, Азове, Новочеркасске и Батайске');

    return arrTitle.join(' ');
  };
  const getSEODescription = () => {
    const arrDescription = [];

    arrDescription.push(
      `${
        query.path && query.path[0] && query.path[0] === 'rent' ? 'Аренда' : 'Продажа'
      } вторичного жилья в Ростове-на-Дону, Азове, Новочеркасске, Батайске.`
    );

    arrDescription.push(
      query.path && query.path[0] && query.path[0] === 'rent' ? 'Снять' : 'Купить'
    );
    if (
      query.path &&
      query.path[1] &&
      ['flats', 'rooms', 'houses', 'sites', 'commercial'].includes(query.path[1])
    ) {
      switch (query.path[1]) {
        default:
        case 'flats':
          if (query.path[2] && ['studio', '1k', '2k', '3k', '4k'].includes(query.path[2])) {
            switch (query.path[2]) {
              case 'studio':
                arrDescription.push('квартиру-студию вторичку');
                break;

              case '1k':
                arrDescription.push('однокомнатную квартиру вторичку');
                break;

              case '2k':
                arrDescription.push('двухкомнатную квартиру вторичку');
                break;

              case '3k':
                arrDescription.push('трехкомнатную квартиру вторичку');
                break;

              case '4k':
                arrDescription.push('четырехкомнатную квартиру вторичку');
                break;

              default:
                break;
            }
          } else {
            arrDescription.push('квартиру вторичку');
          }

          break;

        case 'rooms':
          arrDescription.push('комнату');
          break;

        case 'houses':
          arrDescription.push('дом');
          break;

        case 'sites':
          arrDescription.push('участок');
          break;

        case 'commercial':
          arrDescription.push('коммерческую недвижимость');
          break;
      }
    } else {
      arrDescription.push('квартиру вторичку');
    }

    arrDescription.push('недорого: цены, свежие объявления, срочная продажа.');

    return arrDescription.join(' ');
  };
  const onSortChange = (e) => {
    const selectedOption = [...e.target.options].filter((x) => x.selected)[0];
    const groupedSelected = {
      sort: selectedOption.value.split('/')[0],
      direction: selectedOption.value.split('/')[1]
    };

    setObjectsState({ type: 'SET_PAGINATION', payload: { page: 1 } });
    setObjectsState({
      type: 'SET_SORTING',
      payload: {
        sort: groupedSelected.sort,
        sortDirection: groupedSelected.direction
      }
    });
  };
  const openMobileFilters = () => {
    setObjectsState({
      type: 'SET_STATE',
      payload: { ...objectsState.state, isFiltersMobileVisible: true }
    });
  };

  return (
    <Fragment>
      <NextSeo
        title={getSEOTitle()}
        description={getSEODescription()}
        openGraph={{
          url: 'https://landlord.ru',
          type: 'website',
          title: getSEOTitle(),
          description: getSEODescription(),
          images: [
            {
              url: '/images/og-preview.png'
            }
          ],
          site_name: 'LANDLORD'
        }}
      />
      <BreadcrumbJsonLd itemListElements={breadcrumbsGenerator(query)} />
      <Header />
      <main>
        <ObjectsFilter />
        <ObjectsFilterMobile />
        {!objectsState.state.isFiltersMobileVisible && (
          <div className={styles.filterMobileActions}>
            <Button onClick={openMobileFilters} color="black">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              <span className={styles.filterMobileButtonText}>Фильтры</span>
              {isAnyFilters(objectsState.filter) && (
                <span className={styles.filterMobileButtonMark} />
              )}
            </Button>
            <Button
              style={{ width: 52, height: 52, marginLeft: 4 }}
              withShadow
              color="yellow"
              onClick={() => router.push('/objects/map')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
                <line x1="8" y1="2" x2="8" y2="18" />
                <line x1="16" y1="6" x2="16" y2="22" />
              </svg>
            </Button>
            <Button style={{ width: 52, height: 52, marginLeft: 4 }} withShadow color="yellow">
              <select
                value={`${objectsState.sorting.sort || 'price.value'}/${
                  objectsState.sorting.sortDirection || 'price.asc'
                }`}
                style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, opacity: 0 }}
                onChange={onSortChange}
              >
                <option value="last-update.date">По актуальности</option>
                <option value="price.value/asc">Цена по возрастанию</option>
                <option value="price.value/desc">Цена по убыванию</option>
              </select>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  d="M8 5V19"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
                <path
                  d="M16 19V5"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
                <path
                  d="M12 14L8 19L4 14"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
                <path
                  d="M12.0039 10L16.0039 5L20.0039 10"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </svg>
            </Button>
          </div>
        )}
        <ObjectsCatalog />
        <Splitter
          title="Не нашли что искали?"
          subtitle="Оставьте свой номер телефона и мы поможем Вам с поиском объектов"
        />
        <ObjectsSEO />
      </main>
      <Footer seo={getSEOTitle()} />
    </Fragment>
  );
};

export const getServerSideProps = ({ query }) => {
  return {
    props: {
      query: {
        path: query.path ? query.path.split(',') : []
      }
    }
  };
};

export default ObjectsPage;
